import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  Search,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  ChevronRight,
  X,
  Wallet,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import customerCurrentAccountService, { CustomerCurrentAccount } from '../services/customerCurrentAccountService';
import customersService, { Customer } from '../services/clientsService';
import { formatMoney } from '../utils/format';
import { showError } from '../components/Alerts';

// ==================== TIPOS ====================

interface AccountRow {
  account: CustomerCurrentAccount;
  customer: Customer;
}

// ==================== HELPERS ====================

const formatCurrency = (amount: number) => `$ ${formatMoney(amount)}`;

const getCustomerName = (customer: Customer): string => {
  if (customer.type === 'PERSON') return `${customer.firstName} ${customer.lastName}`;
  return customer.businessName;
};

// ==================== COMPONENTE PRINCIPAL ====================

export default function CustomerAccounts() {
  const navigate = useNavigate();

  const [rows, setRows] = useState<AccountRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [accounts, customers] = await Promise.all([
        customerCurrentAccountService.getAllAccounts(),
        customersService.getAllCustomers(),
      ]);

      const customerMap = new Map<number, Customer>(
        customers.map((c) => [c.id, c])
      );

      const combined: AccountRow[] = accounts
        .map((acc) => {
          const customer = customerMap.get(acc.customerId);
          return customer ? { account: acc, customer } : null;
        })
        .filter((r): r is AccountRow => r !== null);

      setRows(combined);
    } catch {
      showError('Error al cargar las cuentas corrientes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // ==================== FILTRADO ====================

  const filtered = rows.filter((r) => {
    const name = getCustomerName(r.customer);
    return name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const totalPositive = filtered.filter((r) => r.account.balance >= 0).length;
  const totalNegative = filtered.filter((r) => r.account.balance < 0).length;

  // ==================== RENDER ====================

  return (
    <div className="w-full h-full p-4 sm:p-6">
      <div className="overflow-hidden border rounded-xl shadow-sm bg-card border-border">

        {/* ── Header ── */}
        <div className="px-4 py-5 border-b sm:px-6 border-border">
          <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <Wallet className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">Cuentas Corrientes</h1>
                <p className="mt-1 text-sm text-muted-foreground">
                  Estado de cuenta corriente de todos los clientes
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-semibold">
                <Users className="w-3.5 h-3.5" />
                {rows.length} cliente{rows.length !== 1 ? 's' : ''}
              </span>
              <Button variant="outline" size="sm" onClick={loadData} disabled={loading}>
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Actualizar
              </Button>
            </div>
          </div>
        </div>

        {/* ── Cuerpo ── */}
        <div className="p-4 space-y-5 sm:p-6">

          {/* Stats rápidas */}
          {!loading && rows.length > 0 && (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-border bg-background">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <Users className="w-4 h-4 text-primary" />
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Total</p>
                  <p className="text-lg font-bold text-foreground">{filtered.length}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-border bg-background">
                <div className="w-8 h-8 rounded-lg bg-success/10 flex items-center justify-center shrink-0">
                  <TrendingUp className="w-4 h-4 text-success" />
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Saldo positivo</p>
                  <p className="text-lg font-bold text-success">{totalPositive}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-border bg-background col-span-2 sm:col-span-1">
                <div className="w-8 h-8 rounded-lg bg-destructive/10 flex items-center justify-center shrink-0">
                  <TrendingDown className="w-4 h-4 text-destructive" />
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Saldo negativo</p>
                  <p className="text-lg font-bold text-destructive">{totalNegative}</p>
                </div>
              </div>
            </div>
          )}

          {/* Búsqueda */}
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar cliente..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 rounded-lg bg-background border-border focus-visible:ring-primary"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Lista */}
          {loading ? (
            <div className="flex flex-col items-center justify-center py-24">
              <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground mb-3" />
              <p className="text-sm text-muted-foreground">Cargando cuentas corrientes...</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <div className="mx-auto mb-5 w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
                <Wallet className="w-8 h-8 text-primary" />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-foreground">
                {searchTerm ? 'No se encontraron clientes' : 'Sin cuentas corrientes'}
              </h3>
              {searchTerm && (
                <Button variant="outline" size="sm" onClick={() => setSearchTerm('')} className="mt-4">
                  Limpiar búsqueda
                </Button>
              )}
            </div>
          ) : (
            <div className="border rounded-xl border-border overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/40">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Cliente</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Ingresos</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Pagos</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Saldo</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/60">
                  {filtered.map(({ account, customer }) => {
                    const name = getCustomerName(customer);
                    const isNegative = account.balance < 0;
                    const isZero = account.balance === 0;

                    return (
                      <tr
                        key={account.id}
                        className="hover:bg-muted/30 transition-colors cursor-pointer"
                        onClick={() => navigate(`/clients/${customer.id}/current-account`)}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                              <span className="text-xs font-bold text-primary">
                                {name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-foreground truncate">{name}</p>
                              {customer.cuit && (
                                <p className="text-xs text-muted-foreground">CUIT {customer.cuit}</p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-muted-foreground">
                          {formatCurrency(account.totalIncome)}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-muted-foreground">
                          {formatCurrency(account.totalPayments)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className={`tabular-nums font-semibold ${
                            isNegative ? 'text-destructive' : isZero ? 'text-muted-foreground' : 'text-success'
                          }`}>
                            {isNegative ? '-' : ''}{formatCurrency(Math.abs(account.balance))}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <ChevronRight className="w-4 h-4 text-muted-foreground ml-auto" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
